import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import './ConsultoriaMicrosoftAzurePage.css';

const PAGE_URL = 'https://e-asy.es/servicios/consultoria-microsoft-azure';

const FAQ_ITEMS = [
  {
    question: '¿Sois Microsoft Solutions Partner?',
    answer:
      'Estamos en el roadmap de certificación Microsoft Solutions Partner. Lo mencionamos con transparencia: es una especialización en curso, no un sello que presentemos como ya obtenido. El trabajo —cuentas, infraestructura de proyectos y operación— sí lo hacemos hoy.',
  },
  {
    question: '¿Solo Microsoft, o también Google y AWS?',
    answer:
      'La línea cubre Microsoft 365 y Azure, Google Workspace, Dynamics 365 Business Central y AWS. Elegimos stack según lo que ya usáis y lo que podéis operar sin un departamento IT propio.',
  },
  {
    question: '¿Solo nos gestionáis las cuentas de correo y las licencias?',
    answer:
      'No. Ordenar 365 o Workspace es el punto de partida. También montamos y operamos la infraestructura de los proyectos (Azure o AWS: entornos, accesos, costes, lo que hay que alojar) y actuamos como equipo tecnológico de la empresa desde fuera: criterios, mantenimiento y alguien que responde cuando no hay departamento IT interno.',
  },
  {
    question: '¿La cuota de desarrollo entra en esta ficha?',
    answer:
      'No. Aquí están las cuentas, la identidad, Azure o AWS y la operación del día a día. El desarrollo a medida dentro de la gestión, mes a mes y por una cuota del orden de una nómina, es ingeniería a cuota (outsourcing tecnológico): e-asy.es/servicios/ingenieria-a-cuota.',
  },
  {
    question: '¿Esto es lo mismo que infraestructura Cloud y DevOps?',
    answer:
      'No. Cloud y DevOps es contenedores, CI/CD y alta disponibilidad para equipos que despliegan software a diario. Esta línea es ser el equipo tecnológico de la pyme: cuentas, identidad, ERP cuando encaja, e infraestructura de los proyectos en Azure o AWS, sin montar un departamento interno. Si el cuello es Kubernetes, esa ficha es la otra.',
  },
  {
    question: '¿Encaja con Kit Consulting o Kit Digital?',
    answer:
      'Sí. Si llegáis por una ayuda de digitalización, esta es la continuidad: la subvención cubre consultoría o implantación; aquí aterrizamos Microsoft 365, Azure, Workspace o Business Central en un plan que se puede operar.',
  },
  {
    question: '¿Implantáis Dynamics 365 Business Central o solo Vulpin?',
    answer:
      'Las dos, según el caso. Business Central cuando el ERP tiene que vivir en el entorno Microsoft (ficha: e-asy.es/servicios/business-central). Vulpin cuando el núcleo es sociosanitario o un expediente de categoría especial. No empujamos BC por partnership.',
  },
  {
    question: '¿Sois especialistas Azure o solo administráis Microsoft 365?',
    answer:
      'Azure es infraestructura de proyectos: entornos, accesos, costes y lo que hay que alojar (incluida una app de IA). Microsoft 365 es el día a día. Ficha específica: e-asy.es/servicios/azure. Kubernetes y CI/CD siguen en Cloud y DevOps.',
  },
];

const ConsultoriaMicrosoftAzurePage = ({ darkMode, toggleDarkMode }) => {
  const ofertas = [
    {
      titulo: 'Microsoft 365 y Azure',
      descripcion:
        'Correo, identidad y archivos, y también la infraestructura de los proyectos en Azure: entornos, accesos y lo que hay que alojar de verdad. No es solo dar de alta usuarios.',
      puntos: [
        'Migración de correo, archivos e identidad',
        'Infraestructura de proyectos en Azure (entornos, redes, costes visibles)',
        'Operación continua: copias, permisos y alguien que responde',
      ],
      href: '/servicios/azure',
      cta: 'Ver ficha Azure',
    },
    {
      titulo: 'Google Workspace',
      descripcion:
        'Correo, Drive y colaboración para equipos que ya viven en Google o quieren salir de un hosting frágil. Incluye operación: no nos limitamos a crear usuarios y desaparecer.',
      puntos: [
        'Dominio, usuarios, políticas y permisos que se pueden explicar',
        'Migración de buzones y documentos',
        'Criterio y mantenimiento: qué queda en Workspace y quién lo opera',
      ],
    },
    {
      titulo: 'Business Central',
      descripcion:
        'Dynamics 365 Business Central cuando el ERP tiene que hablar con Microsoft 365 y con el resto de la operación. Si Vulpin encaja mejor, te lo decimos; no empujamos BC por partnership.',
      puntos: [
        'Evaluación: Business Central, Vulpin u otra base',
        'Implantación y conexión con el entorno Microsoft',
        'Formación al equipo que facturará y comprará de verdad',
      ],
      href: '/servicios/business-central',
      cta: 'Ver ficha Business Central',
    },
    {
      titulo: 'AWS',
      descripcion:
        'Cuando la carga no pinta en Azure o ya estáis en Amazon: infraestructura de proyecto ordenada, no solo una cuenta con un técnico que “ya sabe”. Accesos, costes y operación a medida de una pyme.',
      puntos: [
        'Entorno de proyecto: cuenta, identidad y costes visibles',
        'Alojamiento de aplicaciones y datos que hay que alojar',
        'Operación y handover para que no dependáis de un único técnico',
      ],
    },
  ];

  const comoActuamos = [
    {
      titulo: 'Cuentas y día a día',
      descripcion:
        'Microsoft 365 o Google Workspace: correo, identidad, archivos y permisos que se pueden explicar. Que el lunes el equipo trabaje, no que “la nube esté en marcha”.',
    },
    {
      titulo: 'Infraestructura de proyectos',
      descripcion:
        'Azure o AWS para lo que hay que alojar: entornos, accesos, copias y costes a la vista. No un PowerPoint de arquitectura; la máquina, la API o el entorno donde corre el proyecto.',
    },
    {
      titulo: 'Equipo tecnológico externo',
      descripcion:
        'Actuamos como el equipo de tecnología de la empresa desde fuera: criterio, operación y alguien que responde. Sin contratar un departamento IT interno ni dejarlo todo en un único técnico “que ya conoce el tema”.',
    },
  ];

  const paraQuien = [
    {
      titulo: 'Pyme sin departamento IT',
      descripcion:
        'No tenéis (ni queréis) un equipo interno. Necesitáis correo, cloud y que alguien lleve la infraestructura de los proyectos como si fuera vuestro departamento, desde fuera.',
    },
    {
      titulo: 'Quien ya usa Microsoft o Google a medias',
      descripcion:
        'Licencias sueltas, cuentas personales mezcladas con las de empresa, y proyectos en un servidor “que ya está”. Hay que ordenar cuentas e infra, no empezar de cero.',
    },
    {
      titulo: 'Quien llega con Kit Consulting o Kit Digital',
      descripcion:
        'La ayuda cubre consultoría o implantación. Aquí convertimos el bono en un entorno concreto: 365, Azure, Workspace o BC, con un plan posterior a la justificación.',
    },
  ];

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: PAGE_URL,
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    }),
    []
  );

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Equipo tecnológico externo: Microsoft 365, Azure y Workspace',
    description:
      'Cuentas (Microsoft 365 y Google Workspace), infraestructura de proyectos en Azure o AWS, y operación para pymes sin departamento IT. Fichas específicas de Azure y Business Central. Roadmap de Microsoft Solutions Partner en curso.',
    url: PAGE_URL,
    serviceType: 'Consultoría cloud y partners',
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Canarias' },
      { '@type': 'Country', name: 'España' },
    ],
    hasPart: [
      {
        '@type': 'Service',
        name: 'Consultoría Azure para pymes',
        url: 'https://e-asy.es/servicios/azure',
      },
      {
        '@type': 'Service',
        name: 'Implantación Dynamics 365 Business Central',
        url: 'https://e-asy.es/servicios/business-central',
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://e-asy.es/' },
      { '@type': 'ListItem', position: 2, name: 'Servicios', item: 'https://e-asy.es/servicios' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Consultoría Microsoft, Azure y partners',
        item: PAGE_URL,
      },
    ],
  };

  return (
    <>
      <SEO
        title="Equipo tecnológico externo: Microsoft 365, Azure y Workspace | E-ASY"
        description="Cuentas 365 y Workspace, infra de proyectos en Azure y operación para pymes. Si buscáis solo Azure o Business Central, hay ficha propia. Roadmap Solutions Partner en curso. Servicio en remoto en toda España, con sede en Tenerife."
        keywords="equipo IT externo pymes, Microsoft 365 Tenerife, Google Workspace Canarias, consultoría Microsoft empresas, Azure y 365 para pymes, E-ASY"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-partners-faq" data={faqSchema} />
      <JsonLd id="jsonld-partners-service" data={serviceSchema} />
      <JsonLd id="jsonld-partners-breadcrumb" data={breadcrumbSchema} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`partners-cloud-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="partners-hero">
          <div className="partners-hero-content">
            <div className="partners-hero-text">
              <h1 className="partners-hero-title">
                Equipo tecnológico externo: 365, Azure, Workspace y Business Central
              </h1>
              <p className="partners-hero-subtitle">
                No es solo dar de alta cuentas. Llevamos{' '}
                <strong>Microsoft 365</strong>, <strong>Azure</strong>,{' '}
                <strong>Google Workspace</strong>, <strong>Business Central</strong>{' '}
                y <strong>AWS</strong>: el día a día, la{' '}
                <strong>infraestructura de los proyectos</strong> y el rol de{' '}
                <strong>equipo tecnológico de la empresa desde fuera</strong>.
              </p>
              <p className="partners-hero-subtitle">
                Estamos en el <strong>roadmap de Microsoft Solutions Partner</strong>:
                especialización en curso, no un sello que vendamos como ya
                conseguido. El trabajo —cuentas, infra, operación— sí lo hacemos
                hoy.
              </p>
              <div className="partners-hero-cta">
                <Link to="/contacto" className="partners-hero-button primary">
                  Agenda una consultoría gratuita
                </Link>
                <Link
                  to="/solicitud-kit-consulting"
                  className="partners-hero-button secondary"
                >
                  Llegas por Kit Consulting
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="partners-que-es">
          <div className="partners-que-es-content">
            <div className="partners-que-es-header">
              <h2 className="partners-que-es-title">Qué ofrecemos</h2>
              <p className="partners-que-es-subtitle">
                No es un catálogo de logos ni un servicio de “os damos de alta
                el Outlook”. Ordenamos cuentas e identidad, montamos la
                infraestructura de los proyectos en Azure o AWS y nos quedamos
                como equipo tecnológico externo: criterio, operación y
                alguien al otro lado. Microsoft 365, Azure, Google Workspace,
                Business Central y AWS, por fases.
              </p>
              <p className="partners-que-es-subtitle">
                La certificación Microsoft Solutions Partner está{' '}
                <strong>en progreso</strong>. Hasta que esté, no la usamos como
                reclamo cerrado. Si lo que necesitáis es Kubernetes y CI/CD, esa
                ficha es{' '}
                <Link to="/servicios/infraestructura-cloud-devops">
                  Infraestructura cloud y DevOps
                </Link>
                . Si lo que falta es desarrollo dentro de la gestión, por una
                cuota mensual, eso es{' '}
                <Link to="/servicios/ingenieria-a-cuota">ingeniería a cuota</Link>
                .
              </p>
            </div>
          </div>
        </section>

        <section className="partners-especialistas">
          <div className="partners-especialistas-content">
            <article id="azure" className="partners-especialista">
              <h2 className="partners-especialista-title">
                Especialistas Azure para pymes
              </h2>
              <p className="partners-especialista-text">
                Azure aquí no es un logo ni un alta de suscripción. Es la{' '}
                <strong>infraestructura de los proyectos</strong>: entornos,
                redes, accesos, copias y costes a la vista. Lo que hay que
                alojar de verdad —una API, una app, un agente de IA, un entorno
                de pruebas— con alguien que responde cuando no hay departamento
                IT interno.
              </p>
              <p className="partners-especialista-text">
                Si el cuello es Kubernetes, pipelines CI/CD y alta
                disponibilidad para un equipo que despliega software a diario,
                esa ficha es{' '}
                <Link to="/servicios/infraestructura-cloud-devops">
                  Infraestructura cloud y DevOps
                </Link>
                . Si lo que tenéis es un prototipo de IA que hay que poner en
                Azure, el despliegue está en{' '}
                <Link to="/servicios/ia-en-produccion">IA en producción</Link>
                ; nosotros montamos el suelo cloud.
              </p>
              <Link to="/servicios/azure" className="partners-especialista-cta">
                Ficha de consultoría Azure →
              </Link>
            </article>
            <article id="business-central" className="partners-especialista">
              <h2 className="partners-especialista-title">
                Implantación Dynamics 365 Business Central
              </h2>
              <p className="partners-especialista-text">
                <strong>Business Central</strong> cuando el ERP tiene que vivir
                en el entorno Microsoft y hablar con 365. Implantación,
                conexión con el resto de la operación y formación al equipo que
                facturará y comprará de verdad — no un partner que configura y
                desaparece.
              </p>
              <p className="partners-especialista-text">
                Si el núcleo es sociosanitario (menores, dependencia, salud),
                no empujamos BC por catálogo: partimos de{' '}
                <Link to="/vulpin">Vulpin Suite</Link>
                . La evaluación es honesta: BC, Vulpin u otra base.
              </p>
              <Link to="/servicios/business-central" className="partners-especialista-cta">
                Ficha de Business Central →
              </Link>
            </article>
          </div>
        </section>

        <section className="partners-beneficios">
          <div className="partners-beneficios-content">
            <div className="partners-beneficios-header">
              <h2 className="partners-beneficios-title">Cómo actuamos</h2>
              <p className="partners-beneficios-subtitle">
                Tres capas, no un alta de usuarios. Podéis contratar una, dos o
                las tres; lo habitual es que vayan juntas.
              </p>
            </div>
            <div className="partners-beneficios-grid">
              {comoActuamos.map((item) => (
                <article key={item.titulo} className="partners-beneficio-card">
                  <h3 className="beneficio-title">{item.titulo}</h3>
                  <p className="beneficio-description">{item.descripcion}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="partners-servicios">
          <div className="partners-servicios-content">
            <div className="partners-servicios-header">
              <h2 className="partners-servicios-title">Línea de partnership</h2>
              <p className="partners-servicios-subtitle">
                Cuatro puertas. Se elige la que ya tenéis o la que menos fricción
                genera, no las cuatro a la vez.
              </p>
            </div>
            <div className="partners-servicios-grid partners-paquetes-grid">
              {ofertas.map((item) => (
                <article key={item.titulo} className="partners-servicio-card">
                  <h3 className="servicio-title">{item.titulo}</h3>
                  <p className="servicio-description">{item.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {item.puntos.map((punto) => (
                        <li key={punto} className="caso-item">
                          {punto}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {item.href && (
                    <Link to={item.href} className="servicio-ficha-link">
                      {item.cta} →
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="partners-sectores">
          <div className="partners-sectores-content">
            <div className="partners-sectores-header">
              <h2 className="partners-sectores-title">Para quién</h2>
              <p className="partners-sectores-subtitle">
                Pymes que necesitan un equipo tecnológico externo: cuentas,
                infraestructura de proyectos y criterio, sin montar un
                departamento interno. Si el núcleo es un ERP sociosanitario,
                partimos de <Link to="/vulpin">Vulpin Suite</Link>.
              </p>
            </div>
            <div className="partners-sectores-grid">
              {paraQuien.map((item) => (
                <article key={item.titulo} className="partners-sector-card">
                  <h3 className="sector-title">{item.titulo}</h3>
                  <p className="sector-description">{item.descripcion}</p>
                </article>
              ))}
            </div>
            <p className="partners-ciber-callout">
              Si llegáis con una ayuda de digitalización, la solicitud está en{' '}
              <Link to="/solicitud-kit-consulting">Kit Consulting</Link>
              {' '}y en{' '}
              <Link to="/kit-digital">Kit Digital</Link>
              . Esta página es lo que viene después: el entorno concreto, no el
              formulario del bono.
            </p>
          </div>
        </section>

        <section className="partners-faq">
          <div className="partners-faq-content">
            <div className="partners-faq-header">
              <h2 className="partners-faq-title">Preguntas frecuentes</h2>
            </div>
            <div className="partners-faq-list">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="partners-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="partners-cta">
          <div className="partners-cta-content">
            <h2 className="partners-cta-title">
              ¿Necesitáis un equipo tecnológico sin montarlo dentro?
            </h2>
            <p className="partners-cta-subtitle">
              Revisamos cuentas, infra de proyectos y operación. Un plan que se
              pueda llevar desde fuera, sin fingir que solo hace falta un alta
              de Outlook.
            </p>
            <div className="partners-cta-buttons">
              <Link to="/contacto" className="partners-cta-button primary">
                Agenda una consultoría gratuita
              </Link>
              <Link to="/servicios" className="partners-cta-button secondary">
                Ver todos los servicios
              </Link>
            </div>
          </div>
        </section>
      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default ConsultoriaMicrosoftAzurePage;
